/**
 * Job-type tracks ("personas"): the switcher in the top bar, and the screen to
 * manage them. A track is a resume + a search + its own applications; every
 * signed-in screen reads the one in use, so switching here changes the whole
 * app. Switching (and creating, which switches to the new track) reloads the
 * page — the one certain way to bring every screen onto the new track at once.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { ErrorList } from "./fields";

export interface Track {
  id: string;
  name: string;
  active: boolean;
  resumeLabel: string | null;
  createdAt: string;
}

function messages(e: unknown): string[] {
  return e instanceof ApiError ? e.messages : [(e as Error).message];
}

/** The switcher for the top bar: the track in use, and a menu to switch or manage. */
export function TrackSwitcher({ tracks, onManage }: { tracks: Track[]; onManage: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const active = tracks.find((t) => t.active) ?? tracks[0];
  if (!active) return null;

  const switchTo = async (id: string) => {
    if (id === active.id) { setOpen(false); return; }
    setBusy(true); setErr(null);
    try { await api(`/api/tracks/${id}/active`, { method: "PUT" }); window.location.reload(); }
    // Keep the menu open and say what happened, rather than closing silently as if the click did nothing.
    catch (e) { setErr(messages(e).join(" ") || "Couldn't switch tracks."); setBusy(false); }
  };

  return (
    <div className="track-switch">
      <button className="track-current" onClick={() => setOpen((v) => !v)} disabled={busy}
              aria-haspopup="listbox" aria-expanded={open} title="Switch job-type track">
        <span className="track-name">{active.name}</span>
        <span className="track-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <>
          <div className="track-scrim" onClick={() => setOpen(false)} />
          <div className="track-menu" role="listbox" aria-label="Your job-type tracks">
            {tracks.map((t) => (
              <button key={t.id} className={`track-opt${t.active ? " on" : ""}`} role="option"
                      aria-selected={t.active} disabled={busy} onClick={() => switchTo(t.id)}>
                <span className="track-check" aria-hidden="true">{t.active ? "✓" : ""}</span>
                <span className="track-opt-name">{t.name}</span>
              </button>
            ))}
            {err && <p className="err small">{err}</p>}
            <div className="track-menu-foot">
              <button className="step-link small" disabled={busy}
                      onClick={() => { setOpen(false); onManage(); }}>Manage tracks…</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The management screen: switch, rename, remove, and add a track. */
export default function TracksScreen({ onChanged, onBack }: { onChanged: () => void; onBack: () => void }) {
  const [tracks, setTracks] = useState<Track[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoadError(null);
    api<{ tracks: Track[] }>("/api/tracks")
      .then((r) => setTracks(r.tracks))
      .catch((e) => setLoadError(messages(e)));
  }, []);
  useEffect(load, [load]);

  /** Switch, create and delete can change which track is in use -- reload so every screen follows. */
  const reloadCall = async (path: string, init: RequestInit) => {
    setBusy(true); setErrors([]);
    try { await api(path, init); window.location.reload(); }
    catch (e) { setErrors(messages(e)); setBusy(false); }
  };

  /** Rename only changes the list -- update it in place and keep the top bar in sync. */
  const listCall = async (path: string, init: RequestInit) => {
    setBusy(true); setErrors([]);
    try { const r = await api<{ tracks: Track[] }>(path, init); setTracks(r.tracks); onChanged(); }
    catch (e) { setErrors(messages(e)); }
    finally { setBusy(false); }
  };

  const rename = (id: string) => {
    void listCall(`/api/tracks/${id}`, { method: "PUT", body: JSON.stringify({ name: draft }) });
    setRenaming(null);
  };

  const create = () => {
    const name = newName.trim();
    if (!name) return;
    void reloadCall("/api/tracks", { method: "POST", body: JSON.stringify({ name }) });
  };

  if (!tracks) {
    // A failed load is not a slow load: without this branch the screen sat on
    // "Loading…" forever on any /api/tracks failure, error swallowed, no retry.
    if (loadError) {
      return (
        <>
          <header className="bar">
            <div><h1 className="tight">Your job-type tracks</h1></div>
            <button className="btn ghost small" onClick={onBack}>Back</button>
          </header>
          <ErrorList errors={loadError} />
          <p><button className="btn" onClick={load}>Try again</button></p>
        </>
      );
    }
    return <p className="muted">Loading…</p>;
  }

  return (
    <>
      <header className="bar">
        <div>
          <h1 className="tight">Your job-type tracks</h1>
          <p className="muted small">Each track is its own resume, search and set of applications — a Test Manager track and an IT Security track, say. The one in use drives every screen; switch with the selector in the top bar.</p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      <ErrorList errors={errors} />

      <ul className="shelf-list">
        {tracks.map((t) => (
          <li key={t.id} className={t.active ? "active" : ""}>
            <label className="shelf-pick">
              <input type="radio" name="active-track" checked={t.active} disabled={busy}
                     onChange={() => { if (!t.active) void reloadCall(`/api/tracks/${t.id}/active`, { method: "PUT" }); }} />
              <span className="cap">{t.active ? "In use" : "Use this"}</span>
            </label>
            <div className="shelf-body">
              {renaming === t.id ? (
                <span className="shelf-rename">
                  <input className="input" value={draft} disabled={busy} autoFocus
                         onChange={(e) => setDraft(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") rename(t.id); if (e.key === "Escape") setRenaming(null); }} />
                  <button className="btn small" disabled={busy || !draft.trim()} onClick={() => rename(t.id)}>Save</button>
                  <button className="btn ghost small" disabled={busy} onClick={() => setRenaming(null)}>Cancel</button>
                </span>
              ) : (
                <span className="shelf-name">{t.name}</span>
              )}
              <span className="muted small">{t.resumeLabel ? `Resume: ${t.resumeLabel}` : "No resume yet"}</span>
            </div>
            <span className="shelf-actions">
              {renaming !== t.id && <button className="step-link small" disabled={busy} onClick={() => { setRenaming(t.id); setDraft(t.name); }}>Rename</button>}
              {confirmRemove === t.id ? (
                <>
                  <button className="step-link small err" disabled={busy}
                          onClick={() => { void reloadCall(`/api/tracks/${t.id}`, { method: "DELETE" }); setConfirmRemove(null); }}>Remove for good</button>
                  <button className="step-link small" disabled={busy} onClick={() => setConfirmRemove(null)}>Keep</button>
                </>
              ) : (
                tracks.length > 1 && <button className="step-link small" disabled={busy} onClick={() => setConfirmRemove(t.id)}>Remove</button>
              )}
            </span>
          </li>
        ))}
      </ul>

      <div className="track-new">
        <span className="field-label">Add a track</span>
        <div className="row">
          <input className="input" value={newName} placeholder="e.g. IT Security" disabled={busy}
                 onChange={(e) => setNewName(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
          <button className="btn" disabled={busy || !newName.trim()} onClick={create}>Add</button>
        </div>
        <p className="muted small">A new track starts empty and becomes the one in use — give it a resume and a search once you switch to it.</p>
      </div>
    </>
  );
}
